import type { BrandingColors } from '../types/branding.types'
import { contrastRatio, readableTextFor } from './colors'

/** Mínimo recomendado para texto de leitura (WCAG AA). */
export const MIN_TEXT_CONTRAST = 4.5

interface PaletteCheck {
  id: string
  label: string
  foreground: keyof BrandingColors
  background: keyof BrandingColors
}

const PALETTE_CHECKS: PaletteCheck[] = [
  {
    id: 'page',
    label: 'Texto da página sobre o fundo',
    foreground: 'foreground',
    background: 'background',
  },
  {
    id: 'card',
    label: 'Texto dos cartões sobre o fundo dos cartões',
    foreground: 'cardForeground',
    background: 'card',
  },
  {
    id: 'popover',
    label: 'Texto dos menus sobre o fundo dos menus',
    foreground: 'popoverForeground',
    background: 'popover',
  },
  {
    id: 'primary',
    label: 'Texto sobre a cor principal',
    foreground: 'primaryForeground',
    background: 'primary',
  },
  {
    id: 'secondary',
    label: 'Texto sobre a cor secundária',
    foreground: 'secondaryForeground',
    background: 'secondary',
  },
  {
    id: 'accent',
    label: 'Texto sobre o destaque',
    foreground: 'accentForeground',
    background: 'accent',
  },
  {
    id: 'muted',
    label: 'Texto secundário sobre o fundo suave',
    foreground: 'mutedForeground',
    background: 'muted',
  },
  {
    id: 'muted-page',
    label: 'Texto secundário sobre o fundo da página',
    foreground: 'mutedForeground',
    background: 'background',
  },
  {
    id: 'destructive',
    label: 'Texto sobre ações destrutivas',
    foreground: 'destructiveForeground',
    background: 'destructive',
  },
  {
    id: 'link',
    label: 'Cor principal como texto sobre o fundo da página',
    foreground: 'primary',
    background: 'background',
  },
]

export interface PaletteCheckResult {
  id: string
  label: string
  ratio: number
  ok: boolean
  fixKey: keyof BrandingColors
}

export function checkPalette(colors: BrandingColors): PaletteCheckResult[] {
  return PALETTE_CHECKS.map(({ id, label, foreground, background }) => {
    const ratio = contrastRatio(colors[foreground], colors[background])
    return { id, label, ratio, ok: ratio >= MIN_TEXT_CONTRAST, fixKey: foreground }
  })
}

/**
 * Corrige só os textos com contraste baixo, trocando-os por branco ou quase preto, o que se ler melhor sobre
 * todos os fundos em que o texto aparece. A cor principal usada como texto não é trocada sozinha: a pessoa
 * precisa escolher outra.
 */
export function fixPaletteContrast(colors: BrandingColors): Partial<BrandingColors> {
  const changes: Partial<BrandingColors> = {}
  const failing = new Set(
    checkPalette(colors)
      .filter((check) => !check.ok && check.fixKey !== 'primary')
      .map((check) => check.fixKey),
  )
  for (const key of failing) {
    const backgrounds = PALETTE_CHECKS.filter((check) => check.foreground === key).map(
      (check) => colors[check.background],
    )
    changes[key] = readableTextFor(...backgrounds)
  }
  return changes
}

export function formatRatio(ratio: number) {
  return `${ratio.toFixed(1).replace('.', ',')}:1`
}
